import React, {useMemo} from 'react';
import {Paper, Typography} from '@mui/material';
import {SxProps, Theme} from '@mui/system';

interface HighlightedNarrativeProps {
    narrative: string;
    highlightPhrases: string[];
    sx?: SxProps<Theme>;
    typographySx?: SxProps<Theme>;
}

/** A run of text, and whether it is one of the phrases asked for. */
export interface TextSegment {
    text: string;
    highlight: boolean;
}

// Some sources store content with literal \n escape sequences rather than real newlines.
// Phrases from node mappings may also carry leading/trailing ellipsis markers from truncation.
// Normalise both so exact matching works across all sources.
const normalizeText = (text: string): string => text.replace(/\\n/g, '\n');
const normalizePhrase = (phrase: string): string =>
    phrase.replace(/^\.\.\.[\s]*/g, '').replace(/[\s]*\.\.\.$/g, '').replace(/\\n/g, '\n').trim();

/**
 * Splits text around the phrases, longest phrase first so a longer match wins over a sub-phrase.
 * Exported because more than one view marks up matched words, and they should agree on what counts
 * as a match.
 */
export function segmentText(text: string, phrases: string[]): TextSegment[] {
    if (!text) return [];

    const normalizedText = normalizeText(text);
    const unique = Array.from(new Set(phrases.map(normalizePhrase).filter(p => p.length > 0)));
    if (unique.length === 0) return [{text: normalizedText, highlight: false}];

    // Longest first so longer matches take priority over sub-phrases
    const sorted = unique.sort((a, b) => b.length - a.length);
    const pattern = sorted.map(p => p.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|');
    const regex = new RegExp(`(${pattern})`, 'gi');

    const segments: TextSegment[] = [];
    let lastIndex = 0;
    let match: RegExpExecArray | null;

    while ((match = regex.exec(normalizedText)) !== null) {
        if (match.index > lastIndex) {
            segments.push({text: normalizedText.slice(lastIndex, match.index), highlight: false});
        }
        segments.push({text: match[0], highlight: true});
        lastIndex = match.index + match[0].length;
    }

    if (lastIndex < normalizedText.length) {
        segments.push({text: normalizedText.slice(lastIndex), highlight: false});
    }

    return segments;
}

export default function HighlightedNarrative({narrative, highlightPhrases, sx, typographySx}: HighlightedNarrativeProps): React.ReactElement {
    const segments = useMemo(() => segmentText(narrative, highlightPhrases), [narrative, highlightPhrases]);
    const hasHighlights = highlightPhrases.length > 0;

    return (
        <Paper
            elevation={0}
            sx={{
                p: 2,
                bgcolor: 'grey.50',
                border: '1px solid',
                borderColor: hasHighlights ? 'warning.light' : 'divider',
                borderRadius: 1,
                transition: 'border-color 0.2s ease',
                '& mark': {
                    backgroundColor: '#fef08a',
                    padding: '1px 3px',
                    borderRadius: '3px',
                    fontWeight: 500,
                    fontStyle: 'normal',
                    animation: 'phraseHighlight 0.2s ease',
                },
                '@keyframes phraseHighlight': {
                    from: {backgroundColor: 'transparent'},
                    to: {backgroundColor: '#fef08a'},
                },
                ...sx,
            }}
        >
            <Typography
                variant="body2"
                component="p"
                sx={{lineHeight: 1.75, whiteSpace: 'pre-wrap', m: 0, ...typographySx}}
            >
                {segments.map((seg, i) =>
                    seg.highlight
                        ? <mark key={i}>{seg.text}</mark>
                        : <React.Fragment key={i}>{seg.text}</React.Fragment>
                )}
            </Typography>
        </Paper>
    );
}
